import { useState, useEffect } from "react";
import { User } from "./types";
import { validateHybridSession } from "./hybridAuthOperations";
import { ensureHybridSession } from "./ensureHybridSession";
export const useAuthState = () => {
  // Session validation state - starts false, becomes true after validation
  const [isSessionValid, setIsSessionValid] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(true);

  // User and authentication state
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('currentUser');
    if (!savedUser) return null;
    const parsed = JSON.parse(savedUser);
    // Ensure company field exists for users saved before company was added
    if (parsed && !parsed.company) {
      parsed.company = 'luijten_vvz';
    }
    return parsed;
  });
  
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  
  const [selectedPackers, setSelectedPackers] = useState<string[]>(() => {
    const savedPackers = localStorage.getItem('selectedPackers');
    return savedPackers ? JSON.parse(savedPackers) : [];
  });

  // Validate session on mount and periodically, auto-restore if needed
  useEffect(() => {
    const validateSession = async () => {
      setIsValidating(true);
      
      const sessionToken = localStorage.getItem('auth_session_token');
      const expiresAt = localStorage.getItem('auth_session_expires');
      
      // Quick client-side check first
      if (!sessionToken || !expiresAt) {
        console.log("[useAuthState] No session token found");
        
    // Auto-restore if user is authenticated but token is missing
        if (isAuthenticated && user?.id) {
          console.log("[useAuthState] User is authenticated, auto-restoring session...");
          const result = await ensureHybridSession(user.id);
          if (result.success) {
            console.log("[useAuthState] Session auto-restored successfully");
            setIsSessionValid(true);
            window.dispatchEvent(new Event('session-restored'));
            setIsValidating(false);
            return;
          } else {
            // User is no longer active/registered - force logout
            console.log("[useAuthState] Session restore failed, forcing logout");
            setUser(null);
            setIsAuthenticated(false);
            localStorage.removeItem('currentUser');
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('selectedPackers');
          }
        }
        
        setIsSessionValid(false);
        setIsValidating(false);
        return;
      }
      
      // Check if expired client-side
      if (new Date() > new Date(expiresAt)) {
        console.log("[useAuthState] Session expired (client-side check)");
        localStorage.removeItem('auth_session_token');
        localStorage.removeItem('auth_session_expires');
        
        // Auto-restore if user is authenticated
        if (isAuthenticated && user?.id) {
          console.log("[useAuthState] User is authenticated, auto-restoring expired session...");
          const result = await ensureHybridSession(user.id);
          if (result.success) {
            console.log("[useAuthState] Expired session auto-restored successfully");
            setIsSessionValid(true);
            window.dispatchEvent(new Event('session-restored'));
            setIsValidating(false);
            return;
          } else {
            console.log("[useAuthState] Expired session restore failed, forcing logout");
            setUser(null);
            setIsAuthenticated(false);
            localStorage.removeItem('currentUser');
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('selectedPackers');
          }
        }
        
        setIsSessionValid(false);
        setIsValidating(false);
        return;
      }
      
      // Server-side validation
      try {
        const isValid = await validateHybridSession();
        console.log("[useAuthState] Session validation result:", isValid);
        setIsSessionValid(isValid);
        
        if (!isValid) {
          // Clear invalid session
          localStorage.removeItem('auth_session_token');
          localStorage.removeItem('auth_session_expires');
          
          // Auto-restore if user is authenticated
          if (isAuthenticated && user?.id) {
            console.log("[useAuthState] Server validation failed, auto-restoring...");
            const result = await ensureHybridSession(user.id);
            if (result.success) {
              console.log("[useAuthState] Session auto-restored after server validation failure");
              setIsSessionValid(true);
              window.dispatchEvent(new Event('session-restored'));
            }
          }
        }
      } catch (error) {
        console.error("[useAuthState] Session validation error:", error);
        setIsSessionValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateSession();
    
    // Re-validate every 5 minutes
    const interval = setInterval(validateSession, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user?.id]);

  // Persist state to localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem('currentUser', JSON.stringify(user));
      localStorage.setItem('isAuthenticated', 'true');
    } else {
      localStorage.removeItem('currentUser');
      localStorage.setItem('isAuthenticated', 'false');
    }
  }, [user, isAuthenticated]);

  // Update session validity when user logs in
  useEffect(() => {
    if (user && isAuthenticated) {
      const sessionToken = localStorage.getItem('auth_session_token');
      const expiresAt = localStorage.getItem('auth_session_expires');
      
      // Check BOTH presence AND expiration
      if (sessionToken && expiresAt && new Date() < new Date(expiresAt)) {
        setIsSessionValid(true);
        setIsValidating(false);
      }
    }
  }, [user, isAuthenticated]);

  return {
    user, 
    setUser, 
    isAuthenticated, 
    setIsAuthenticated, 
    selectedPackers, 
    setSelectedPackers,
    isSessionValid,
    isValidating
  };
};
